import { useNavigate, useLocation } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { Icon } from '@/components/ui/Icon'
import { ThemedSelect } from '@/components/ui/ThemedSelect'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { setUserTheme } from '@/api/users'
import type { Theme } from '@/types'

const GROUP_TABS = [
  { path: '/wishlist', label: 'Wishlist',        icon: 'wishlist'    as const },
  { path: '/playing',  label: 'Playing',         icon: 'controller'  as const },
  { path: '/played',   label: 'Played',          icon: 'star'        as const },
  { path: '/common-library', label: 'Common',    icon: 'friends'     as const },
  { path: '/recs',     label: 'Recommendations', icon: 'spark'       as const },
]
const THEME_OPTIONS = [
  { value: 'midnight', label: 'Midnight' },
  { value: 'abyss', label: 'Abyss' },
  { value: 'ember', label: 'Ember' },
  { value: 'forest', label: 'Forest' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'mono', label: 'Mono' },
  { value: 'matrix', label: 'Matrix' },
  { value: 'paper', label: 'Paper' },
  { value: 'sakura', label: 'Sakura' },
  { value: 'solarized-light', label: 'Solarized Light' },
] as const

interface TopbarProps {
  onMenuOpen: () => void
}

export function Topbar({ onMenuOpen }: TopbarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, setTheme } = useUIStore()
  const isAuthed = useAuthStore((s) => s.user !== null)
  const themeMutation = useMutation({ mutationFn: setUserTheme })

  const handleThemeChange = (next: Theme) => {
    setTheme(next)
    if (isAuthed) themeMutation.mutate(next)
  }

  const isLibrary = location.pathname === '/library'
  const isFriends = location.pathname === '/friends'
  const isGroupSection = !isLibrary && !isFriends

  return (
    <div className="cg-topbar">
      <button className="cg-hamburger cg-icon-btn" onClick={onMenuOpen} aria-label="Open menu">
        <Icon name="menu" size={16} />
      </button>
      <div className="cg-topbar-tabs">
        {isGroupSection && GROUP_TABS.map((tab) => (
          <button
            key={tab.path}
            className="cg-tab"
            data-active={location.pathname === tab.path}
            onClick={() => navigate(tab.path)}
          >
            <Icon name={tab.icon} size={12} /> {tab.label}
          </button>
        ))}
        {isLibrary && (
          <button className="cg-tab" data-active="true">
            <Icon name="library" size={12} /> My Steam library
          </button>
        )}
        {isFriends && (
          <button className="cg-tab" data-active="true">
            <Icon name="friends" size={12} /> Friends & Groups
          </button>
        )}
      </div>

      <div className="cg-topbar-spacer" />

      <ThemedSelect
        value={theme}
        options={THEME_OPTIONS}
        onChange={handleThemeChange}
        className="cg-theme-select"
        ariaLabel="Color theme"
      />
    </div>
  )
}
